// A membership billed by instalments, on its sheet (BL-009): "Bills $20.00 monthly, next on Dec 3,
// 2026.", where the latest bill stands with its invoice, and for the owner and the manager the next
// bill moved to another day (memberships.setBillDate) or billing paused with why and resumed
// (memberships.pauseBilling). While the company mirrors ServiceTitan the server refuses both, in words.

import { useState } from 'react';
import { Button, Label, Link, TextField } from '@flowology/ui-web/fl';
import { client } from '../api.ts';
import { dayText } from '../books/model.ts';
import { errorText } from '../books/ui.tsx';
import { docHref } from '../shared/doc-link.ts';
import { FormActs } from './member-parts.tsx';
import { BILL_WORDS, billDateProblem, billDays, labelKind, type Billing } from './model.ts';

interface ChangeOut {
  billing: Billing;
  message: string;
}

/** The Billing value in the sheet's list: the summary, then the latest bill when it needs a look. */
export function BillingValue({ billing: b }: { billing: Billing }) {
  const latest = b.latest && b.latest.state !== 'paid' ? b.latest : null;
  return (
    <>
      {b.summary}
      {b.card && <span className="bp-member-quiet bp-member-block">Charged to {b.card.label}.</span>}
      {b.paused?.reason && <span className="bp-member-quiet bp-member-block">Paused: {b.paused.reason}</span>}
      {latest && (
        <span className="bp-member-block">
          <Label kind={labelKind(BILL_WORDS[latest.state][1])}>{BILL_WORDS[latest.state][0]}</Label>{' '}
          <span className="bp-member-quiet">the {dayText(latest.billOn)} bill</span>
          {latest.note && <span className="bp-member-quiet bp-member-block">{latest.note}</span>}
          {latest.invoiceId && (
            <span className="bp-member-block">
              <Link href={docHref({ kind: 'invoice', id: latest.invoiceId })}>Open its invoice</Link>
            </span>
          )}
        </span>
      )}
    </>
  );
}

/** Change next bill date… and Pause billing… (or Resume billing), for the owner and the manager. */
export function BillingActions({
  billing: b,
  membershipId,
  name,
  today,
  onChanged,
}: {
  billing: Billing;
  membershipId: string;
  /** "Ana Ruiz’s Comfort Club membership". */
  name: string;
  today: string;
  /** After a change: what was said, and the billing as it is now. */
  onChanged: (said: { message: string }, billing: Billing) => void;
}) {
  const [open, setOpen] = useState<'date' | 'pause' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const done = (r: ChangeOut) => {
    setOpen(null);
    onChanged({ message: r.message }, r.billing);
  };
  const resume = async () => {
    setBusy(true);
    setError(null);
    try {
      done(await client.call<ChangeOut>('memberships.pauseBilling', { membershipId, paused: false }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  if (open === 'date')
    return (
      <BillDateForm
        billing={b}
        membershipId={membershipId}
        name={name}
        today={today}
        onClose={() => setOpen(null)}
        onDone={done}
      />
    );
  if (open === 'pause')
    return <PauseForm membershipId={membershipId} name={name} onClose={() => setOpen(null)} onDone={done} />;
  return (
    <div className="bp-member-acts">
      {!b.paused && b.nextBillOn && <Button onPress={() => setOpen('date')}>Change next bill date…</Button>}
      {b.paused ? (
        <Button isBusy={busy} onPress={() => void resume()}>
          {busy ? 'Resuming…' : 'Resume billing'}
        </Button>
      ) : (
        b.nextBillOn && <Button onPress={() => setOpen('pause')}>Pause billing…</Button>
      )}
      {error && <p className="bp-member-bad">{error}</p>}
    </div>
  );
}

function BillDateForm({
  billing: b,
  membershipId,
  name,
  today,
  onClose,
  onDone,
}: {
  billing: Billing;
  membershipId: string;
  name: string;
  today: string;
  onClose: () => void;
  onDone: (r: ChangeOut) => void;
}) {
  const days = billDays(today, b.endsOn);
  const [on, setOn] = useState(b.nextBillOn ?? today);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const problem = billDateProblem(on, days, dayText);
  const save = async () => {
    setTried(true);
    if (problem) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await client.call<ChangeOut>('memberships.setBillDate', { membershipId, nextBillOn: on }));
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="bp-member-form"
      aria-label={`Change the next bill date of ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <TextField
        type="date"
        width="date"
        label="Next bill"
        min={days.min}
        max={days.max}
        value={on}
        onChange={setOn}
        error={tried && problem ? problem : null}
      />
      <p className="bp-member-line">
        The bills after it follow on that day of the month (a shorter month: its last day). Nothing is billed
        for the days in between.
      </p>
      <FormActs
        back={onClose}
        busy={busy}
        error={error}
        label={busy ? 'Moving…' : 'Move the next bill'}
        onSave={() => void save()}
      />
    </form>
  );
}

function PauseForm({
  membershipId,
  name,
  onClose,
  onDone,
}: {
  membershipId: string;
  name: string;
  onClose: () => void;
  onDone: (r: ChangeOut) => void;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tried, setTried] = useState(false);
  const save = async () => {
    setTried(true);
    if (!reason.trim()) return;
    setBusy(true);
    setError(null);
    try {
      onDone(
        await client.call<ChangeOut>('memberships.pauseBilling', {
          membershipId,
          paused: true,
          reason: reason.trim(),
        }),
      );
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="bp-member-form"
      aria-label={`Pause billing for ${name}`}
      onSubmit={(e) => {
        e.preventDefault();
        if (!busy) void save();
      }}
    >
      <p className="bp-member-line">
        Nothing is billed until it’s resumed. Resumed, it bills next on its own day: the bills while it was
        paused aren’t billed later. The membership itself stays as it is.
      </p>
      <TextField
        label="Why"
        maxLength={500}
        value={reason}
        onChange={setReason}
        placeholder="Waiting on a new card, paid ahead by cheque…"
        error={tried && !reason.trim() ? 'Say why billing is paused.' : null}
      />
      <FormActs
        back={onClose}
        busy={busy}
        error={error}
        label={busy ? 'Pausing…' : 'Pause billing'}
        onSave={() => void save()}
      />
    </form>
  );
}
